import { NavLink, Outlet } from "react-router-dom";
import {
  LayoutDashboard, Radio, Server, Route, Shield, ListChecks,
  PhoneCall, AlertTriangle, FileCode2, Users, LogOut, ChevronsLeft, Activity, Menu, Megaphone, Hash, HeartPulse, Archive, ClipboardList, AudioLines, HardDriveUpload, History
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import voxyraSbcLogo from "@/assets/voxyra-sbc-logo.png";

const NAV = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard", testid: "nav-dashboard", end: true },
  { to: "/operadoras", icon: Radio, label: "Operadoras", testid: "nav-operadoras" },
  { to: "/dids", icon: Hash, label: "DIDs", testid: "nav-dids" },
  { to: "/ipbxs", icon: Server, label: "IPBX externos", testid: "nav-ipbxs" },
  { to: "/clientes-cids", icon: Hash, label: "Clientes / CIDs", testid: "nav-clientes-cids" },
  { to: "/rotas", icon: Route, label: "Rotas / LCR", testid: "nav-rotas" },
  { to: "/anuncios", icon: Megaphone, label: "Anúncios", testid: "nav-anuncios" },
  { to: "/audios-sistema", icon: AudioLines, label: "Áudios do sistema", testid: "nav-system-audios" },
  { to: "/gravacoes", icon: HardDriveUpload, label: "Gravações", testid: "nav-recordings" },
  { to: "/acl", icon: Shield, label: "ACL", testid: "nav-acl" },
  { to: "/cdr", icon: ListChecks, label: "Relatório de Ligações", testid: "nav-cdr" },
  { to: "/live", icon: PhoneCall, label: "Chamadas Ativas", testid: "nav-live" },
  { to: "/antifraud", icon: AlertTriangle, label: "Anti-Fraude", testid: "nav-antifraud" },
  { to: "/voxyra-sbc", icon: FileCode2, label: "Voxyra SBC", testid: "nav-voxyra-sbc" },
  { to: "/health", icon: HeartPulse, label: "Saúde do SBC", testid: "nav-health" },
  { to: "/backups", icon: Archive, label: "Backups", testid: "nav-backups" },
  { to: "/versions", icon: History, label: "Versões", testid: "nav-versions" },
  { to: "/audit", icon: ClipboardList, label: "Auditoria", testid: "nav-audit" },
  { to: "/usuarios", icon: Users, label: "Usuários", testid: "nav-usuarios" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [uptime, setUptime] = useState("--");
  const [health, setHealth] = useState({ active: 0 });
  const [carrierAlert, setCarrierAlert] = useState(null);
  const [pendingConfig, setPendingConfig] = useState(null);
  const [applyingConfig, setApplyingConfig] = useState(false);

  useEffect(() => {
    const start = Date.now();
    const tick = () => {
      const s = Math.floor((Date.now() - start) / 1000);
      const d = Math.floor(s / 86400);
      const h = Math.floor((s % 86400) / 3600);
      const m = Math.floor((s % 3600) / 60);
      setUptime(`${d}d ${h.toString().padStart(2, "0")}h ${m.toString().padStart(2, "0")}m`);
    };
    tick();
    const iv = setInterval(tick, 30000);

    const load = async () => {
      try {
        const { data } = await api.get("/metrics/dashboard");
        setHealth({ active: data.active_channels });
      } catch (_err) { /* ignore */ }
    };
    const beepAlert = () => {
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (!AudioCtx) return;
        const ctx = new AudioCtx();
        const oscillator = ctx.createOscillator();
        const gain = ctx.createGain();
        oscillator.type = "sine";
        oscillator.frequency.value = 880;
        gain.gain.value = 0.03;
        oscillator.connect(gain);
        gain.connect(ctx.destination);
        oscillator.start();
        window.setTimeout(() => {
          oscillator.stop();
          ctx.close().catch(() => {});
        }, 220);
      } catch (_err) { /* ignore */ }
    };
    const loadCarrierHealth = async () => {
      try {
        const { data } = await api.get("/health");
        const operadoras = data?.operadoras || {};
        const offline = Number(operadoras.offline || 0);
        const degraded = Number(operadoras.degraded || 0);
        const items = Array.isArray(operadoras.items) ? operadoras.items : [];
        const problematic = items.filter((item) => item?.status === "offline" || item?.status === "degraded");
        const nextAlert = (offline > 0 || degraded > 0)
          ? {
              offline,
              degraded,
              total: Number(operadoras.enabled || operadoras.total || 0),
              names: problematic.slice(0, 3).map((item) => item.nome || item.host || item.id).filter(Boolean),
            }
          : null;
        setCarrierAlert((current) => {
          const hadProblem = !!current && ((current.offline || 0) > 0 || (current.degraded || 0) > 0);
          const hasProblem = !!nextAlert;
          if (hasProblem && !hadProblem) {
            beepAlert();
          }
          return nextAlert;
        });
      } catch (_err) { /* ignore */ }
    };
    const loadPending = async () => {
      try {
        const { data } = await api.get("/voxyra-sbc/pending", { params: { _ts: Date.now() } });
        setPendingConfig(data);
      } catch (_err) { /* ignore */ }
    };
    const onPending = () => loadPending();
    window.addEventListener("voxyra-config-pending", onPending);
    load();
    loadCarrierHealth();
    loadPending();
    const iv2 = setInterval(load, 4000);
    const ivHealth = setInterval(loadCarrierHealth, 5000);
    const iv3 = setInterval(loadPending, 6000);
    return () => { clearInterval(iv); clearInterval(iv2); clearInterval(ivHealth); clearInterval(iv3); window.removeEventListener("voxyra-config-pending", onPending); };
  }, []);

  const applyConfig = async () => {
    setApplyingConfig(true);
    try {
      await api.post("/voxyra-sbc/apply");
      const { data } = await api.get("/voxyra-sbc/pending", { params: { _ts: Date.now() } });
      setPendingConfig(data);
    } finally {
      setApplyingConfig(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="app-layout">
      {/* Sidebar */}
      <aside
        className={`transition-all duration-200 border-r border-[color:var(--border-default)] bg-[color:var(--bg-surface)] flex flex-col ${collapsed ? "w-14" : "w-60"}`}
        data-testid="sidebar"
      >
        <div className="h-14 flex items-center gap-2 px-3 border-b border-[color:var(--border-default)]">
          <div className={`bg-[color:var(--bg-elevated)] border border-[color:var(--border-default)] rounded-sm overflow-hidden flex items-center justify-center ${collapsed ? "w-8 h-8" : "w-36 h-9 px-2"}`}>
            <img
              src={voxyraSbcLogo}
              alt="Voxyra SBC"
              className={`${collapsed ? "w-8 h-8 object-cover object-top" : "h-7 w-full object-contain"}`}
            />
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] leading-none">Session Border Controller</div>
              <div className="text-sm font-semibold leading-tight">VOXYRA SBC</div>
            </div>
          )}
          <button
            data-testid="sidebar-toggle"
            className="p-1 text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]"
            onClick={() => setCollapsed(!collapsed)}
          >
            {collapsed ? <Menu size={14} /> : <ChevronsLeft size={14} />}
          </button>
        </div>

        <nav className="flex-1 py-3 px-2 space-y-0.5">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              className={({ isActive }) => `side-link ${isActive ? "active" : ""}`}
            >
              <item.icon size={16} className="shrink-0" />
              {!collapsed && <span className="truncate">{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {!collapsed && (
          <div className="p-3 border-t border-[color:var(--border-default)]">
            <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] mb-1">Uptime</div>
            <div className="font-mono text-xs text-[color:var(--accent-green)]" data-testid="sidebar-uptime">{uptime}</div>
          </div>
        )}
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="h-14 border-b border-[color:var(--border-default)] bg-[color:var(--bg-surface)] flex items-center px-4 gap-4">
          <div className="flex items-center gap-2">
            <span className="led led-green" />
            <span className="text-xs font-mono uppercase tracking-widest text-[color:var(--text-secondary)]" data-testid="server-status">
              Server ONLINE
            </span>
          </div>
          <div className="text-[color:var(--border-default)]">|</div>
          <div className="flex items-center gap-2">
            <Activity size={14} className="text-[color:var(--accent-amber)]" />
            <span className="text-xs font-mono text-[color:var(--text-secondary)]" data-testid="topbar-active-channels">
              {health.active} ligações ativas
            </span>
          </div>
          <div className="flex-1" />
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-xs font-medium" data-testid="user-name">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]" data-testid="user-role">
                {user?.role}
              </div>
            </div>
            <div className="w-8 h-8 rounded-full bg-[color:var(--bg-elevated)] border border-[color:var(--border-default)] flex items-center justify-center text-xs font-mono">
              {(user?.name || "?").charAt(0).toUpperCase()}
            </div>
            <button
              data-testid="logout-btn"
              className="sbc-btn"
              onClick={logout}
              title="Sair"
            >
              <LogOut size={14} />
            </button>
          </div>
        </header>

        {pendingConfig?.pending && (
          <div className="border-b border-[rgba(245,158,11,0.35)] bg-[rgba(245,158,11,0.10)] px-4 py-3 flex items-center justify-between gap-3" data-testid="pending-config-banner">
            <div>
              <div className="text-xs font-semibold text-[color:var(--accent-amber)]">Configurações pendentes no Voxyra SBC</div>
              <div className="text-[11px] text-[color:var(--text-secondary)] font-mono">{pendingConfig.reason || "Alteração operacional"} · {pendingConfig.changed_at ? new Date(pendingConfig.changed_at).toLocaleString("pt-BR") : "agora"}</div>
            </div>
            <button className="sbc-btn sbc-btn-primary" onClick={applyConfig} disabled={applyingConfig} data-testid="apply-config-btn">
              {applyingConfig ? "Aplicando..." : "Aplicar configurações"}
            </button>
          </div>
        )}

        {carrierAlert && (
          <div className="border-b border-[rgba(239,68,68,0.35)] bg-[rgba(239,68,68,0.08)] px-4 py-3 flex items-center justify-between gap-3" data-testid="carrier-monitor-banner">
            <div className="min-w-0">
              <div className="text-xs font-semibold text-[color:var(--accent-red)]">Aviso de operadoras no SBC</div>
              <div className="text-[11px] text-[color:var(--text-secondary)] font-mono">
                {carrierAlert.offline} offline · {carrierAlert.degraded} degradada(s) · {carrierAlert.total} monitoradas
                {carrierAlert.names?.length ? ` · ${carrierAlert.names.join(", ")}` : ""}
              </div>
            </div>
            <NavLink className="sbc-btn sbc-btn-primary" to="/operadoras">
              Ver monitoramento
            </NavLink>
          </div>
        )}

        {/* Content */}
        <main className="flex-1 overflow-auto p-6 fade-in" data-testid="main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
